import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';

const References = ({
	currentMentions,
	entityReferences,
	removeMention,
	isCollapsed,
	toggleCollapsed,
	readOnly = false,
}) => {
	const listRef = useRef(null);
	
	const [expandedEntities, setExpandedEntities] = useState({});
	const [showLinksSection, _setShowLinksSection] = useState(false);

	/**
	 * Toggle entity expansion in references panel
	 * Expands/collapses entity details in the reference panel
	 *
	 * @param {string} entityId - ID of the entity to toggle
	 */
	const toggleEntityExpansion = (entityId) => {
		setExpandedEntities((prev) => ({
			...prev,
			[entityId]: !prev[entityId],
		}));
	};

	/**
	 * Render entity fields based on entity type
	 * 
	 * @param {Object} entity - The entity to render fields for
	 * @returns {JSX.Element} - The rendered entity fields
	 */
	const renderEntityFields = (entity) => {
		// Handle case where entity might not have all expected fields
		if (!entity || Object.keys(entity).length === 0) {
			return (
				<Typography variant="body2" color="textSecondary">
					No information available for this entity.
				</Typography>
			);
		}

		switch (entity.type) {
			case 'account':
				return (
					<div className="entity-details">
						{entity.investor_type && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Type: </strong>{entity.investor_type}
							</Typography>
						)}
						{(entity.billing_city || entity.billing_state) && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Location: </strong> 
								{entity.billing_city ? entity.billing_city : ''}
								{entity.billing_city && entity.billing_state ? ', ' : ''}
								{entity.billing_state ? entity.billing_state : ''}
								{entity.billing_country ? `, ${entity.billing_country}` : ''}
							</Typography>
						)}
						{entity.website && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Website: </strong>
								<Link href={entity.website} target="_blank" rel="noopener noreferrer">
									{entity.website.replace(/^https?:\/\//, '')}
								</Link>
							</Typography>
						)}
						{entity.phone && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Phone: </strong>{entity.phone}
							</Typography>
						)}
					</div>
				);

			case 'deal':
				return (
					<div className="entity-details">
						{entity.deal_investment_amount && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Investment Amount: </strong>${Number(entity.deal_investment_amount).toLocaleString()}
							</Typography>
						)}
						{entity.deal_size && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Deal Size: </strong>${Number(entity.deal_size).toLocaleString()}
							</Typography>
						)}
						{entity.deal_stage && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Stage: </strong>{entity.deal_stage}
							</Typography>
						)}
						{entity.deal_type && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Type: </strong>{entity.deal_type}
							</Typography>
						)}
						{(entity.deal_city || entity.deal_state) && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Location: </strong> 
								{entity.deal_city ? entity.deal_city : ''}
								{entity.deal_city && entity.deal_state ? ', ' : ''}
								{entity.deal_state ? entity.deal_state : ''}
								{entity.deal_country ? `, ${entity.deal_country}` : ''}
							</Typography>
						)}
						{entity.deal_date_sourced && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Sourced: </strong>{new Date(entity.deal_date_sourced).toLocaleDateString()}
							</Typography>
						)}
						{entity.deal_last_interaction_date && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Last Interaction: </strong>{new Date(entity.deal_last_interaction_date).toLocaleDateString()}
							</Typography>
						)}
					</div>
				);

			case 'contact':
				return (
					<div className="entity-details">
						{entity.title && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Title: </strong>{entity.title}
							</Typography>
						)}
						{entity.email && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Email: </strong>
								<Link href={`mailto:${entity.email}`}>{entity.email}</Link>
							</Typography>
						)}
						{entity.phone && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Phone: </strong>{entity.phone}
							</Typography>
						)}
						{entity.last_interaction_date && (
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								<strong>Last Interaction: </strong>{new Date(entity.last_interaction_date).toLocaleDateString()}
							</Typography>
						)}
					</div>
				);

			default:
				// If we don't know the entity type, just display all available fields
				return (
					<div className="entity-details">
						{Object.entries(entity)
							.filter(([key, value]) => 
								// Filter out metadata fields and null/undefined values
								!['id', 'type', 'tenant_id'].includes(key) && 
								value !== null && 
								value !== undefined)
							.map(([key, value]) => (
								<Typography key={key} variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
									<strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>{' '}
									{typeof value === 'string' && value.startsWith('http') ? (
										<Link href={value} target="_blank" rel="noopener noreferrer">
											{value.replace(/^https?:\/\//, '')}
										</Link>
									) : (
										value
									)}
								</Typography>
							))}
					</div>
				);
		}
	};

	return (
		<div className={`references-card ${isCollapsed ? 'collapsed' : 'expanded'}`} style={{ display: 'flex', flexDirection: 'column' }}>
			<div className='references-header' onClick={toggleCollapsed}>
				<span className='references-title'>
					References{' '}
					<span className='references-count'>({currentMentions.length})</span>
				</span>
				<span className='references-actions'>
					{/* Icon points down when expanded to indicate you can collapse
					    and points up when collapsed to indicate you can expand */}
					{isCollapsed ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
				</span>
			</div>
			{!isCollapsed && (
				<div className='references-list' ref={listRef}>
					{currentMentions.map((entity) => (
						<div key={entity.id} className='references-entity'>
							<div
								className='references-entity-header'
								onClick={() => toggleEntityExpansion(entity.id)}
							>
								<div className='references-entity-main'>
									<span className='references-entity-name'>{entity.name}</span>
									<span
										className={`references-entity-type ${entity.type === 'research' ? 'research' : entity.type === 'deal' ? 'deal' : entity.type === 'company' ? 'company' : 'other'}`}
									>
										{entity.type === 'research' ? 'Research' : entity.type}
									</span>
								</div>
								<div className='references-entity-actions'>
									{/* Only show remove button if not in read-only mode */}
									{!readOnly && (
										<button
											className='references-remove'
											onClick={(e) => {
												e.stopPropagation();
												removeMention(entity.id);
											}}
										>
											<CloseIcon style={{ fontSize: 14 }} />
										</button>
									)}
									{expandedEntities[entity.id] ? (
										<ExpandLessIcon fontSize='small' />
									) : (
										<ExpandMoreIcon fontSize='small' />
									)}
								</div>
							</div>
							{expandedEntities[entity.id] && (
								<div className='references-entity-expanded'>
									{/* Entity Details Section */}
									<Box sx={{ marginBottom: '8px' }}>
										{renderEntityFields(entity)}
									</Box>
									
									{/* Hidden until we know the best way to generate links */}
									{/* Links Section */}
									{showLinksSection && (
										<>
											<Divider sx={{ margin: '8px 0' }} />

											<Typography 
												variant="subtitle2" 
												component="div" 
												color="textSecondary"
												sx={{ marginBottom: '4px', fontWeight: 'bold' }}
											>
												Links
											</Typography>
											{entityReferences[entity.id] ? (
												<ul className='references-sources'>
													{entityReferences[entity.id].map((ref, idx) => (
														<li key={idx} className='references-source-item'>
															<a
																href={ref.url || '#'}
																target='_blank'
																rel='noopener noreferrer'
																className='references-source-link'
															>
																{ref.title}
																<span className='references-source-meta'>
																	({ref.source})
																</span>
																{ref.confidence && (
																	<span className='references-source-confidence'>
																		{Math.round(ref.confidence * 100)}%
																	</span>
																)}
															</a>
														</li>
													))}
												</ul>
											) : (
												<div className='references-loading'>
													<AccessTimeIcon
														style={{ fontSize: 14, marginRight: 4 }}
													/>
													Loading references...
												</div>
											)}
										</>
									)}
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

References.propTypes = {
	currentMentions: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			type: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired
		})
	),
	entityReferences: PropTypes.objectOf(
		PropTypes.arrayOf(
			PropTypes.shape({
				title: PropTypes.string.isRequired,
				source: PropTypes.string.isRequired,
				url: PropTypes.string.isRequired
			})
		)
	),
	removeMention: PropTypes.func,
	isCollapsed: PropTypes.bool,
	toggleCollapsed: PropTypes.func,
	readOnly: PropTypes.bool
};

export default References;
